'use client';

import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import ImageSequenceHero from '@/components/ImageSequenceHero';

export default function HomeV2() {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [activeStrategy, setActiveStrategy] = useState(1);

  const strategies = [
    {
      num: '01',
      title: 'Web Development',
      desc: 'We craft stunning websites that load fast and look amazing. Your digital home, built with love and clean code!',
      position: 'top',
    },
    {
      num: '02',
      title: 'Branding',
      desc: 'We build your brand, one pixel at a time. With designs that make you stand out. Well, outstanding. Not literally, we promise!',
      position: 'right',
    },
    {
      num: '03',
      title: 'Marketing',
      desc: 'Strategic campaigns that amplify your reach. We turn audiences into loyal customers with data-driven marketing.',
      position: 'left',
    },
    {
      num: '04',
      title: 'Video Production',
      desc: 'Lights, camera, action! We produce captivating videos that tell your story and keep your audience hooked.',
      position: 'bottom',
    },
  ];

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-visible');
          }
        });
      },
      { threshold: 0.1 }
    );
    document.querySelectorAll('.animate-on-scroll').forEach((el) => {
      observerRef.current?.observe(el);
    });
    return () => observerRef.current?.disconnect();
  }, []);

  return (
    <div style={{ overflowX: 'clip' }}>

      {/* ═══════════════════ IMAGE SEQUENCE HERO ═══════════════════ */}
      <ImageSequenceHero />

      {/* ═══════════════════ INNOVATIVE STRATEGIES CIRCLE ═══════════════════ */}
      <section className="ms-strategies">
        <div className="ms-container">
          <div className="ms-strategies-heading">
            <h3>
              Innovative Strategies,<br />Impressive Results
            </h3>
          </div>

          {/* Large dashed circle with service cards */}
          <div className="ms-circle-diagram">
            <svg className="ms-circle-svg" viewBox="0 0 720 720">
              <circle cx="360" cy="360" r="330" stroke="#b8c4ca" strokeWidth="1.5" strokeDasharray="10 10" fill="none" />
            </svg>

            {/* Top — Web Development */}
            <div
              className={`ms-circle-icon top${activeStrategy === 0 ? ' active' : ''}`}
              onClick={() => setActiveStrategy(0)}
            >
              <svg width="32" height="32" fill="none" stroke={activeStrategy === 0 ? 'white' : '#185f74'} strokeWidth="1.5" viewBox="0 0 24 24">
                <rect x="2" y="3" width="20" height="14" rx="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" />
              </svg>
            </div>

            {/* Right — Branding / Design */}
            <div
              className={`ms-circle-icon right${activeStrategy === 1 ? ' active' : ''}`}
              onClick={() => setActiveStrategy(1)}
            >
              <svg width="30" height="30" fill="none" stroke={activeStrategy === 1 ? 'white' : '#185f74'} strokeWidth="1.5" viewBox="0 0 24 24">
                <path d="M12 20h9" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>

            {/* Left — Marketing */}
            <div
              className={`ms-circle-icon left${activeStrategy === 2 ? ' active' : ''}`}
              onClick={() => setActiveStrategy(2)}
            >
              <svg width="30" height="30" fill="none" stroke={activeStrategy === 2 ? 'white' : '#247c97'} strokeWidth="1.5" viewBox="0 0 24 24">
                <path d="M22 12h-4l-3 9L9 3l-3 9H2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>

            {/* Bottom — Video */}
            <div
              className={`ms-circle-icon bottom${activeStrategy === 3 ? ' active' : ''}`}
              onClick={() => setActiveStrategy(3)}
            >
              <svg width="30" height="30" fill="none" stroke={activeStrategy === 3 ? 'white' : '#185f74'} strokeWidth="1.5" viewBox="0 0 24 24">
                <polygon points="23 7 16 12 23 17 23 7" strokeLinecap="round" strokeLinejoin="round" />
                <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
              </svg>
            </div>

            {/* Center content — animated */}
            <div className="ms-circle-center" key={activeStrategy}>
              <span className="ms-circle-num">{strategies[activeStrategy].num}</span>
              <h4>{strategies[activeStrategy].title}</h4>
              <p>{strategies[activeStrategy].desc}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════ ABOUT — Discover Our Journey ═══════════════════ */}
      <section id="about" className="ms-about">
        {/* Discover Our Journey marquee */}
        <div style={{ overflow: 'hidden' }}>
          <div className="animate-marquee">
            <span className="light">Our Journey.</span>
            <span className="dark">Discover Our Journey.</span>
            <span className="light">Our Journey.</span>
            <span className="dark">Discover Our Journey.</span>
          </div>
        </div>

        <div className="ms-container">
          <div className="ms-about-grid">
            {/* Left — megaphone on teal blob */}
            <div className="ms-about-image">
              <div className="ms-about-blob" />
              <div style={{ position: 'relative', zIndex: 10 }}>
                <svg viewBox="0 0 440 500" style={{ width: '320px', height: '360px', filter: 'drop-shadow(0 24px 48px rgba(0,0,0,0.3))' }}>
                  {/* Hand/arm */}
                  <rect x="110" y="270" width="44" height="105" rx="10" fill="#8B5E3C" />
                  <ellipse cx="132" cy="268" rx="28" ry="17" fill="#A0704C" />
                  <rect x="102" y="248" width="13" height="32" rx="6.5" fill="#A0704C" transform="rotate(-5,108,264)" />
                  <rect x="120" y="243" width="12" height="30" rx="6" fill="#A0704C" />
                  <rect x="136" y="246" width="12" height="28" rx="6" fill="#A0704C" />
                  <rect x="150" y="252" width="11" height="24" rx="5.5" fill="#A0704C" />
                  <rect x="104" y="355" width="58" height="42" rx="4" fill="#0e4a5c" />
                  {/* Megaphone body */}
                  <path d="M140 182 L320 88 L320 368 L140 275 Z" fill="url(#megaGrad)" />
                  <ellipse cx="320" cy="228" rx="18" ry="142" fill="#0e7a8a" opacity="0.5" />
                  <ellipse cx="140" cy="228" rx="16" ry="50" fill="#247c97" />
                  <ellipse cx="140" cy="228" rx="9" ry="30" fill="#185f74" opacity="0.3" />
                  <ellipse cx="140" cy="228" rx="18" ry="54" fill="none" stroke="#e05050" strokeWidth="6" opacity="0.6" />
                  {/* Sound waves */}
                  <path d="M340 152 Q378 228 340 305" stroke="#a8d8e8" strokeWidth="3" fill="none" opacity="0.4" strokeLinecap="round" />
                  <path d="M365 118 Q410 228 365 338" stroke="#a8d8e8" strokeWidth="2.5" fill="none" opacity="0.28" strokeLinecap="round" />
                  <path d="M388 85 Q440 228 388 370" stroke="#a8d8e8" strokeWidth="2" fill="none" opacity="0.16" strokeLinecap="round" />
                  <defs>
                    <linearGradient id="megaGrad" x1="0" y1="0" x2="0.8" y2="0.6">
                      <stop offset="0%" stopColor="#38baba" /><stop offset="50%" stopColor="#1e9a9a" /><stop offset="100%" stopColor="#148585" />
                    </linearGradient>
                  </defs>
                </svg>
              </div>
              {/* 15+ Years badge */}
              <div className="ms-years-badge">
                <span className="ms-years-num">15+</span>
                <span className="ms-years-label">Years</span>
              </div>
            </div>

            {/* Right — About text */}
            <div className="ms-about-text">
              <h4>About Us</h4>
              <p>
                We like to think of ourselves as a closely knit family, with abundance of creativity,
                and the confidence to execute what we&apos;ve undertaken. With years of experience to
                accomplish the expertise we have today, while you think of it, we&apos;ve already
                executed it! As a family who supports each other, we step in to lend a hand to
                ensure our word to you is never breached. After all, knowing we are responsible for
                that satisfied smile on your face is what makes it absolutely worth it, in the end!
              </p>
              <a href="#contact" className="ms-btn">
                <span className="ms-btn-icon">
                  <svg width="18" height="18" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                </span>
                <span className="ms-btn-label">Contact Us</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════ SERVICES ═══════════════════ */}
      <section id="services" className="ms-services">
        <div className="ms-container">
          {/* Intro text */}
          <div className="ms-services-intro">
            <p>
              Our services blend creativity with strategic insight, delivering impactful
              solutions that drive results for your business.
            </p>
          </div>

          {/* Grid — lightbulb panel + service list */}
          <div className="ms-services-grid">
            {/* Lightbulb illustration panel */}
            <div className="ms-services-panel">
              <Image src="/images/Group 28.png" alt="Creative services illustration" fill style={{ objectFit: 'cover', borderRadius: '24px' }} />
            </div>

            {/* Service list */}
            <div className="ms-services-list">
              <div className="ms-service-items">
                {[
                  { name: 'WEB DEVELOPMENT' },
                  { name: 'GRAPHIC DESIGN' },
                  { name: 'SOCIAL MEDIA MARKETING' },
                ].map((service) => (
                  <a key={service.name} href="#" className="ms-service-item">
                    <span className="ms-service-name">{service.name}</span>
                    <svg width="20" height="20" fill="none" stroke="#2d2c2b" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                ))}
              </div>
              <a href="#" className="ms-btn" style={{ marginTop: '40px' }}>
                <span className="ms-btn-icon">
                  <svg width="18" height="18" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                </span>
                <span className="ms-btn-label">View All Services</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════ PORTFOLIO ═══════════════════ */}
      <section id="portfolio" className="ms-portfolio">
        <div className="ms-container">
          <div className="ms-portfolio-header">
            <h2 className="ms-portfolio-h2">Portfolio.</h2>
            <h4>Successful Projects</h4>
            <p>
              Brochure? Corporate Videos? Advertisements? Websites? Looking for any or all of
              these? May we break it gently to you? Your search is officially over! Don&apos;t believe us?
              Come; take a look at our diversified portfolio.
            </p>
            <a href="#" className="ms-btn" style={{ marginTop: '32px' }}>
              <span className="ms-btn-icon">
                <svg width="18" height="18" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
              </span>
              <span className="ms-btn-label">View All Works</span>
            </a>
          </div>

          {/* Portfolio Grid */}
          <div className="ms-portfolio-grid">
            {/* Row 1 — 3 equal */}
            {[
              { img: '/images/Mask group.png', label: 'Brand Collateral', desc: 'Brochure Mockup' },
              { img: '/images/Mask group-2.png', label: 'Product Packaging', desc: 'Honey Bottle Design' },
              { img: '/images/Mask group-4.png', label: 'Business Cards', desc: 'Edmonds Cards' },
            ].map((item) => (
              <div key={item.label} className="ms-portfolio-item">
                <div className="ms-portfolio-bg">
                  <Image src={item.img} alt={item.desc} fill style={{ objectFit: 'cover' }} />
                </div>
                <div className="ms-portfolio-tag">{item.label}</div>
                <div className="ms-portfolio-overlay"><span>{item.desc}</span></div>
              </div>
            ))}

            {/* Row 2 — 2 wide + 1 */}
            <div className="ms-portfolio-item wide">
              <div className="ms-portfolio-bg">
                <Image src="/images/Mask group-3.png" alt="Kimia Dates & Cashews" fill style={{ objectFit: 'cover' }} />
              </div>
              <div className="ms-portfolio-tag dark">Packaging Design</div>
              <div className="ms-portfolio-overlay"><span>Kimia Dates &amp; Cashews</span></div>
            </div>
            <div className="ms-portfolio-item">
              <div className="ms-portfolio-bg">
                <Image src="/images/Mask group-5.png" alt="Bezalel Distributors" fill style={{ objectFit: 'cover' }} />
              </div>
              <div className="ms-portfolio-overlay"><span>Brand Identity</span></div>
            </div>

            {/* Row 3 — 1 + 2 wide */}
            <div className="ms-portfolio-item">
              <div className="ms-portfolio-bg">
                <Image src="/images/Mask group-1.png" alt="GA Stationery Set" fill style={{ objectFit: 'cover' }} />
              </div>
              <div className="ms-portfolio-tag dark">Stationery Design</div>
              <div className="ms-portfolio-overlay"><span>GA Stationery Set</span></div>
            </div>
            <div className="ms-portfolio-item wide">
              <div className="ms-portfolio-bg">
                <Image src="/images/Mask group-6.png" alt="GA Business Cards" fill style={{ objectFit: 'cover' }} />
              </div>
              <div className="ms-portfolio-tag">Business Cards</div>
              <div className="ms-portfolio-overlay"><span>GA Business Cards</span></div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════ CORPORATE VIDEO ═══════════════════ */}
      <section className="ms-video">
        <div className="ms-container">
          <div className="ms-video-header">
            <h2 className="ms-video-h2">Corporate<br />Video.</h2>
            <h4>To Video or Not to Video?</h4>
            <p>
              Research says people remember videos longer than words. Educate clients, boost
              awareness, share your passion with a video. Capture the moment, highlight the detail,
              get to the point. You can&apos;t sell anything if you can&apos;t tell anything! Start telling... Like yesterday!
            </p>
            <div className="ms-video-btn-wrap">
              <a href="#" className="ms-btn">
                <span className="ms-btn-icon">
                  <svg width="18" height="18" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                </span>
                <span className="ms-btn-label">View All Works</span>
              </a>
            </div>
          </div>

          {/* Video player */}
          <div className="ms-video-thumb" onClick={() => setVideoPlaying(!videoPlaying)} style={{ cursor: 'pointer' }}>
            {videoPlaying ? (
              <iframe
                src="https://www.youtube.com/embed/pEZi9LHNHFk?autoplay=1&controls=0&modestbranding=1&rel=0&showinfo=0"
                title="MediaSplash Corporate Video"
                allow="autoplay; encrypted-media"
                allowFullScreen
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none', zIndex: 5 }}
              />
            ) : (
              <Image src="/images/video.svg" alt="MediaSplash Video" fill style={{ objectFit: 'cover' }} />
            )}
            {/* Play/Pause button overlay */}
            <div className="ms-play-btn" style={{ zIndex: 10 }}>
              <div className="ms-play-circle">
                {videoPlaying ? (
                  <svg width="26" height="26" fill="white" viewBox="0 0 20 20"><rect x="5" y="3" width="4" height="14" rx="1"/><rect x="11" y="3" width="4" height="14" rx="1"/></svg>
                ) : (
                  <svg width="26" height="26" fill="white" viewBox="0 0 20 20"><path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z"/></svg>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════ MEET THE EXPERTS — Marquee ═══════════════════ */}
      <section className="ms-marquee-section">
        <div style={{ overflow: 'hidden' }}>
          <div className="animate-marquee">
            <span className="light">the Experts.</span>
            <span className="dark">Meet the Experts.</span>
            <span className="light">the Experts.</span>
            <span className="dark">Meet the Experts.</span>
          </div>
        </div>
      </section>

      {/* ═══════════════════ CTA BAR ═══════════════════ */}
      <section id="contact" className="ms-cta">
        <div className="ms-container">
          <div className="ms-cta-inner">
            <p>
              Have a new project? Let&apos;s collaborate and<br />bring it to life together.
            </p>
            <a href="#contact" className="ms-btn" style={{ flexShrink: 0 }}>
              <span className="ms-btn-icon">
                <svg width="18" height="18" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
              </span>
              <span className="ms-btn-label">Contact Us</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
